import { useState, useEffect, useMemo } from "react";
import useSWR from "swr";
import { api, fmtEur, fmtPct, WEEKDAYS_SK } from "@/lib/app";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import PinButton from "@/components/PinButton";

const fetcher = (url) => api.get(url).then((r) => r.data);

// Compute period-over-period change as B (newer) relative to A (older):
//   positive = B grew vs A, negative = B dropped vs A
function changePct(aOlder, bNewer) {
  if (!aOlder) return 0;
  return ((bNewer - aOlder) / aOlder) * 100;
}

function Delta({ value }) {
  if (value === 0) return <span className="text-[var(--text-muted)] tabular text-xs">±0 %</span>;
  const positive = value > 0;
  const cls = positive ? "text-[var(--success)]" : "text-[var(--critical)]";
  return (
    <span className={`tabular text-xs font-medium ${cls}`}>
      {positive ? "▲" : "▼"} {Math.abs(value).toFixed(1)} %
    </span>
  );
}

function KpiCompare({ label, a, b, fmt = fmtEur, higherIsBetter = true, pinProps }) {
  // Direction: B vs A. higherIsBetter controls colour semantics (▲ green when B grew is "good").
  const delta = changePct(a, b);
  const direction = higherIsBetter ? delta : -delta;
  return (
    <div className="card-flat p-4 relative">
      {pinProps && (
        <div className="absolute top-2 right-2">
          <PinButton {...pinProps} />
        </div>
      )}
      <p className="label-eyebrow mb-3 pr-6">{label}</p>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <p className="text-[0.65rem] uppercase tracking-wider text-[var(--text-muted)] mb-1">Obdobie A (staršie)</p>
          <p className="font-display text-xl font-bold tabular">{fmt(a)}</p>
        </div>
        <div>
          <p className="text-[0.65rem] uppercase tracking-wider text-[var(--text-muted)] mb-1">Obdobie B (novšie)</p>
          <p className="font-display text-xl font-bold tabular text-[var(--text-secondary)]">{fmt(b)}</p>
        </div>
      </div>
      <div className="mt-2 pt-2 border-t border-[var(--border-default)] flex items-center justify-between">
        <span className="text-xs text-[var(--text-muted)]">B vs A</span>
        <Delta value={direction} />
      </div>
    </div>
  );
}

export default function PeriodCompare({ storeId, range }) {
  const today = range?.max || "2026-12-31";
  const minDate = range?.min || "2026-01-01";

  // smart defaults: split available range in half
  const mid = useMemo(() => {
    if (!range?.min || !range?.max) return null;
    const m = new Date(range.min);
    const x = new Date(range.max);
    const mid = new Date((m.getTime() + x.getTime()) / 2);
    return mid.toISOString().slice(0, 10);
  }, [range]);

  const [aFrom, setAFrom] = useState("");
  const [aTo, setATo] = useState("");
  const [bFrom, setBFrom] = useState("");
  const [bTo, setBTo] = useState("");

  useEffect(() => {
    if (range?.min && !aFrom) {
      setAFrom(range.min);
      setATo(mid);
      const midNext = new Date(mid);
      midNext.setDate(midNext.getDate() + 1);
      setBFrom(midNext.toISOString().slice(0, 10));
      setBTo(range.max);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, mid]);

  const qsA = aFrom && aTo ? `period_from=${aFrom}&period_to=${aTo}` : null;
  const qsB = bFrom && bTo ? `period_from=${bFrom}&period_to=${bTo}` : null;

  const { data: dataA } = useSWR(qsA ? `/stores/${storeId}/analytics/overview?${qsA}` : null, fetcher);
  const { data: dataB } = useSWR(qsB ? `/stores/${storeId}/analytics/overview?${qsB}` : null, fetcher);

  const chartData = useMemo(() => {
    if (!dataA || !dataB) return [];
    if (dataA.empty || dataB.empty) return [];
    return WEEKDAYS_SK.map((name, wd) => {
      const a = dataA.by_weekday.find((x) => x.weekday === wd) || {};
      const b = dataB.by_weekday.find((x) => x.weekday === wd) || {};
      return { name, A: a.avg_daily_revenue || 0, B: b.avg_daily_revenue || 0 };
    });
  }, [dataA, dataB]);

  if (!range || !range.count) {
    return <div className="card-flat p-8 text-center text-[var(--text-secondary)]">Najprv importuj dáta.</div>;
  }

  const tA = dataA?.totals;
  const tB = dataB?.totals;

  return (
    <div className="space-y-5">
      <div className="card-flat p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <p className="label-eyebrow mb-2">Obdobie A</p>
          <div className="flex gap-2 items-center">
            <input type="date" value={aFrom} min={minDate} max={today} onChange={(e) => setAFrom(e.target.value)} data-testid="cmp-a-from" />
            <span className="text-[var(--text-muted)]">→</span>
            <input type="date" value={aTo} min={minDate} max={today} onChange={(e) => setATo(e.target.value)} data-testid="cmp-a-to" />
          </div>
        </div>
        <div>
          <p className="label-eyebrow mb-2">Obdobie B</p>
          <div className="flex gap-2 items-center">
            <input type="date" value={bFrom} min={minDate} max={today} onChange={(e) => setBFrom(e.target.value)} data-testid="cmp-b-from" />
            <span className="text-[var(--text-muted)]">→</span>
            <input type="date" value={bTo} min={minDate} max={today} onChange={(e) => setBTo(e.target.value)} data-testid="cmp-b-to" />
          </div>
        </div>
      </div>

      {(!dataA || !dataB) && <div className="text-[var(--text-secondary)]">Načítavam…</div>}
      {dataA?.empty && <div className="card-flat p-4 text-sm text-[var(--text-secondary)]">Obdobie A nemá dáta.</div>}
      {dataB?.empty && <div className="card-flat p-4 text-sm text-[var(--text-secondary)]">Obdobie B nemá dáta.</div>}

      {tA && tB && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KpiCompare label="Celková tržba" a={tA.revenue} b={tB.revenue} pinProps={{ storeId, kind: "kpi", label: `Porovnanie A vs B — Celková tržba: ${fmtEur(tA.revenue)} vs ${fmtEur(tB.revenue)}`, data: { metric: `compare_revenue_${aFrom}_${aTo}_vs_${bFrom}_${bTo}`, a: tA.revenue, b: tB.revenue }, period: { from: aFrom, to: aTo }, testId: "pin-compare-revenue" }} />
            <KpiCompare label="Priem. denná tržba" a={tA.avg_daily_revenue} b={tB.avg_daily_revenue} pinProps={{ storeId, kind: "kpi", label: `Porovnanie A vs B — Priem. denná tržba: ${fmtEur(tA.avg_daily_revenue)} vs ${fmtEur(tB.avg_daily_revenue)}`, data: { metric: `compare_avg_daily_${aFrom}_${aTo}_vs_${bFrom}_${bTo}`, a: tA.avg_daily_revenue, b: tB.avg_daily_revenue }, period: { from: aFrom, to: aTo }, testId: "pin-compare-avg-daily" }} />
            <KpiCompare label="€ / osobohodina" a={tA.avg_per_person_hour} b={tB.avg_per_person_hour} pinProps={{ storeId, kind: "kpi", label: `Porovnanie A vs B — €/os.hod: ${fmtEur(tA.avg_per_person_hour)} vs ${fmtEur(tB.avg_per_person_hour)}`, data: { metric: `compare_pph_${aFrom}_${aTo}_vs_${bFrom}_${bTo}`, a: tA.avg_per_person_hour, b: tB.avg_per_person_hour }, period: { from: aFrom, to: aTo }, testId: "pin-compare-pph" }} />
            <KpiCompare label="Efektívnosť" a={tA.efficiency_pct} b={tB.efficiency_pct} fmt={(v) => fmtPct(v, 1)} pinProps={{ storeId, kind: "kpi", label: `Porovnanie A vs B — Efektívnosť: ${fmtPct(tA.efficiency_pct, 1)} vs ${fmtPct(tB.efficiency_pct, 1)}`, data: { metric: `compare_eff_${aFrom}_${aTo}_vs_${bFrom}_${bTo}`, a: tA.efficiency_pct, b: tB.efficiency_pct }, period: { from: aFrom, to: aTo }, testId: "pin-compare-eff" }} />
          </div>

          <div className="card-flat p-5">
            <div className="flex items-start justify-between mb-1 gap-2">
              <h3 className="font-display text-base font-semibold">Priem. denná tržba podľa dňa v týždni</h3>
              <PinButton storeId={storeId} kind="chart" label={`Porovnanie období: ${aFrom}→${aTo} vs ${bFrom}→${bTo}`} data={{ id: `compare_chart_${aFrom}_${aTo}_${bFrom}_${bTo}` }} period={{ from: aFrom, to: aTo }} testId="pin-compare-chart" />
            </div>
            <p className="text-xs text-[var(--text-secondary)] mb-4">Bok po boku: obdobie A vs B.</p>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="#E4E8E5" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#5E6962" }} />
                <YAxis tick={{ fontSize: 11, fill: "#5E6962" }} tickFormatter={(v) => `${v}€`} />
                <Tooltip formatter={(v) => fmtEur(v)} contentStyle={{ background: "white", border: "1px solid #E4E8E5", borderRadius: 6, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="A" name={`A: ${aFrom} → ${aTo}`} fill="#2B4C38" radius={[4, 4, 0, 0]} isAnimationActive={false} />
                <Bar dataKey="B" name={`B: ${bFrom} → ${bTo}`} fill="#C78220" radius={[4, 4, 0, 0]} isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="card-flat overflow-hidden">
            <div className="px-5 pt-5 pb-3">
              <h3 className="font-display text-base font-semibold">Detail podľa dňa v týždni — B vs A</h3>
              <p className="text-xs text-[var(--text-secondary)] mt-1">
                Δ = ako sa zmenilo novšie obdobie <strong>B</strong> oproti staršiemu <strong>A</strong>. ▲ zelená = B narástlo · ▼ červená = B kleslo.
              </p>
            </div>
            <table>
              <thead>
                <tr>
                  <th>Deň</th>
                  <th className="text-right">A — tržba (staršie)</th>
                  <th className="text-right">B — tržba (novšie)</th>
                  <th className="text-right">Δ B vs A</th>
                  <th className="text-right">A €/os.hod</th>
                  <th className="text-right">B €/os.hod</th>
                  <th className="text-right">Δ B vs A</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {WEEKDAYS_SK.map((wd, i) => {
                  const a = dataA.by_weekday.find((x) => x.weekday === i);
                  const b = dataB.by_weekday.find((x) => x.weekday === i);
                  if (!a && !b) return null;
                  const av = a?.avg_daily_revenue || 0;
                  const bv = b?.avg_daily_revenue || 0;
                  const apph = a?.per_person_hour || 0;
                  const bpph = b?.per_person_hour || 0;
                  return (
                    <tr key={i}>
                      <td className="font-medium">{wd}</td>
                      <td className="text-right tabular">{fmtEur(av)}</td>
                      <td className="text-right tabular text-[var(--text-secondary)]">{fmtEur(bv)}</td>
                      <td className="text-right"><Delta value={changePct(av, bv)} /></td>
                      <td className="text-right tabular">{fmtEur(apph)}</td>
                      <td className="text-right tabular text-[var(--text-secondary)]">{fmtEur(bpph)}</td>
                      <td className="text-right"><Delta value={changePct(apph, bpph)} /></td>
                      <td className="text-right">
                        <PinButton
                          storeId={storeId}
                          kind="weekday"
                          label={`Porovnanie ${wd}: A ${fmtEur(av)} (€/os.hod ${fmtEur(apph)}) → B ${fmtEur(bv)} (€/os.hod ${fmtEur(bpph)}), Δ B vs A ${changePct(av, bv).toFixed(1)} %`}
                          data={{ weekday: i, source: "period_compare", aFrom, aTo, bFrom, bTo, av, bv, apph, bpph }}
                          period={{ from: aFrom, to: aTo }}
                          testId={`pin-compare-row-${i}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
